/** Lifetime message limit for browse (unsubscribed) users. */
export const BROWSE_USER_MESSAGE_LIMIT = 5;

/** Legal disclosure the AI disclaimer links out to (opens in a new tab). */
export const PRIVACY_POLICY_URL = '/privacy/ai-tools';

/** Error code returned by the chat API when a browse user exceeds the message limit. */
export const BROWSE_LIMIT_ERROR_CODE = 'BROWSE_LIMIT_EXCEEDED';

/** Returns true if the error (any shape) contains the browse limit error code. */
export function isBrowseLimitError(error: unknown): boolean {
    return JSON.stringify(error ?? '').includes(BROWSE_LIMIT_ERROR_CODE);
}

/** Error code returned by the chat API when a lapsed subscriber must reactivate. */
export const SUBSCRIPTION_REQUIRED_ERROR_CODE = 'SUBSCRIPTION_REQUIRED';

/** Error code returned by the chat API when a user hits the rate limit. */
export const RATE_LIMIT_ERROR_CODE = 'RATE_LIMIT_EXCEEDED';

/** Returns true if the error (any shape) contains the rate limit error code. */
export function isRateLimitError(error: unknown): boolean {
    return JSON.stringify(error ?? '').includes(RATE_LIMIT_ERROR_CODE);
}

/**
 * Sentinel `errorText` the chat stream emits when the conversation exceeds the model's context
 * window. The conv-search-api controller maps the provider's mid-stream context-limit error to this
 * exact string (see CONTEXT_LENGTH_EXCEEDED in conv-search-api ChatStream module) so the frontend
 * can show a "conversation too long — start a new chat" message instead of the generic error.
 */
export const CONTEXT_LENGTH_EXCEEDED = 'CONTEXT_LENGTH_EXCEEDED';

/** Returns true if the error (any shape) contains the context-length-exceeded sentinel. */
export function isContextLengthError(error: unknown): boolean {
    return JSON.stringify(error ?? '').includes(CONTEXT_LENGTH_EXCEEDED);
}

/**
 * Extracts the retryAfter value (seconds) from a rate limit error payload.
 * The payload is typically a JSON string like `{"code":"RATE_LIMIT_EXCEEDED","retryAfter":45,"error":"..."}`.
 * Returns null if the error is not parseable or has no retryAfter.
 */
export function parseRetryAfter(error: unknown): number | null {
    try {
        const str = typeof error === 'string' ? error : JSON.stringify(error ?? '');
        const parsed: unknown = JSON.parse(str);
        if (typeof parsed === 'object' && parsed !== null && 'retryAfter' in parsed) {
            const value = (parsed as { retryAfter: unknown }).retryAfter;
            return typeof value === 'number' ? value : null;
        }
    } catch {
        // not parseable
    }
    return null;
}
